"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.TableSortableHeaderCell = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireWildcard(require("react"));
var _constants = require("../../constants");
var _Table = require("../Table/Table.context");
var _TableHeaderCellLabelWrapper = require("../TableHeaderCellLabelWrapper/TableHeaderCellLabelWrapper");
var _TableSortable = require("../TableSortable/TableSortable.context");
var _TableSortIcon = require("../TableSortIcon/TableSortIcon");
var _TableSortIconWrapper = require("../TableSortIconWrapper/TableSortIconWrapper");
var _VisuallyHidden = require("../VisuallyHidden/VisuallyHidden");
var _TableSortableHeaderCell = require("./TableSortableHeaderCell.styles");
var _excluded = ["children", "index", "onClick", "onKeyDown", "onSortChange", "sortDirection", "sortOrder"];
var _TABLE_SORT_ICONS;
var TABLE_SORT_ICONS = (_TABLE_SORT_ICONS = {}, _TABLE_SORT_ICONS[_constants.TABLE_SORT_DIRECTIONS.ascending] = _TableSortIcon.TableSortIconArrowUp, _TABLE_SORT_ICONS[_constants.TABLE_SORT_DIRECTIONS.descending] = _TableSortIcon.TableSortIconArrowDown, _TABLE_SORT_ICONS[_constants.TABLE_SORT_DIRECTIONS.none] = _TableSortIcon.TableSortIconSort, _TABLE_SORT_ICONS);
var getNextSortDirection = function getNextSortDirection(currentSortDirection, sortOrder) {
  var currentDirectionIndex = sortOrder.indexOf(currentSortDirection);
  var nextSortDirectionIndex = (currentDirectionIndex + 1) % sortOrder.length;
  return sortOrder[nextSortDirectionIndex];
};

/**
 * `Table.SortableHeaderCell` is used in place of `Table.HeaderCell` when a column is sortable.
 *
 * A double-arrow icon appears on hover to indicate a column is sortable. A single-arrow icon
 * indicates which column is currently being sorted on and in which direction.
 *
 * @subcomponents ['Table.Sortable']
 * @since 8.41.0
 */
var TableSortableHeaderCell = /*#__PURE__*/(0, _react.forwardRef)(function (props, forwardedRef) {
  var children = props.children,
    index = props.index,
    onClick = props.onClick,
    onKeyDown = props.onKeyDown,
    onSortChange = props.onSortChange,
    sortDirection = props.sortDirection,
    sortOrder = props.sortOrder,
    rest = (0, _objectWithoutProperties2.default)(props, _excluded);

  // Text alignment vars
  var tableContext = (0, _react.useContext)(_Table.TableContext);
  var textAlign = props.align || tableContext.alignments && tableContext.alignments[index];

  // Sort direction vars
  var sortableContext = (0, _react.useContext)(_TableSortable.TableSortableContext);
  var currentSortDirection = sortDirection || sortableContext.directions && sortableContext.directions[index] || _constants.TABLE_SORT_DIRECTIONS.none;
  var SortIcon = TABLE_SORT_ICONS[currentSortDirection];

  // Use the general `onSortChange` provided by `Table.Sortable` or override if `onSortChange`
  // has been set on this cell
  var onSortCallback = onSortChange || sortableContext.onSortChange && sortableContext.onSortChange || null;
  var onClickCallback = (0, _react.useCallback)(function (e) {
    var nextSortDirection = getNextSortDirection(currentSortDirection, sortOrder);

    // Update `Table.Sortable`'s state with new sort directions array
    if (typeof sortableContext.directions !== 'undefined') {
      var newDirections = new Array(sortableContext.directions.length).fill(_constants.TABLE_SORT_DIRECTIONS.none);
      newDirections[index] = nextSortDirection;
      sortableContext.setDirections(newDirections);
    }

    // Then execute callbacks
    // We're separating onSortChange from onClick to give us flexibility.
    // ex: In a future change, we might want to apply the sorting callback to an
    // icon button instead of the entire table cell.
    if (typeof onSortCallback === 'function') {
      onSortCallback({
        currentSortDirection: currentSortDirection,
        nextSortDirection: nextSortDirection
      }, props);
    }
    if (typeof onClick === 'function') {
      onClick(e);
    }
  }, [currentSortDirection, sortableContext, index, onClick, onSortCallback, sortOrder, props]);
  var onKeyDownCallback = (0, _react.useCallback)(function (e) {
    if (e.keyCode === _constants.KEY_CODES.ENTER || e.keyCode === _constants.KEY_CODES.SPACE) {
      onClickCallback();
      e.preventDefault();
    }
    if (typeof onKeyDown === 'function') {
      onKeyDown(e);
    }
  }, [onClickCallback, onKeyDown]);
  return /*#__PURE__*/_react.default.createElement(_TableSortableHeaderCell.StyledTableSortableHeaderCell, (0, _extends2.default)({
    align: textAlign,
    "aria-sort": _constants.TABLE_SORT_DIRECTIONS[currentSortDirection],
    as: "th",
    onClick: onClickCallback,
    onKeyDown: onKeyDownCallback,
    ref: forwardedRef,
    scope: "col",
    size: tableContext.size,
    sortDirection: currentSortDirection
  }, rest), /*#__PURE__*/_react.default.createElement(_TableHeaderCellLabelWrapper.TableHeaderCellLabelWrapper, {
    role: "button",
    tabIndex: "0"
  },
  /* Uses &#65279; and white-space: nowrap on StyledSortIconWrapper to
                protect against the icon wrapping to the next line by itself. */
  textAlign === 'right' && SortIcon && /*#__PURE__*/_react.default.createElement(_TableSortIconWrapper.TableSortIconWrapper, null, /*#__PURE__*/_react.default.createElement(SortIcon, null), "\uFEFF"), children, textAlign !== 'right' && SortIcon && /*#__PURE__*/_react.default.createElement(_TableSortIconWrapper.TableSortIconWrapper, null, "\uFEFF", /*#__PURE__*/_react.default.createElement(SortIcon, null)), /*#__PURE__*/_react.default.createElement(_VisuallyHidden.VisuallyHidden, null, currentSortDirection === _constants.TABLE_SORT_DIRECTIONS.none ? ', sortable column' : ", sorted " + _constants.TABLE_SORT_DIRECTIONS[currentSortDirection])));
});
exports.TableSortableHeaderCell = TableSortableHeaderCell;
TableSortableHeaderCell.displayName = 'Table.SortableHeaderCell';
TableSortableHeaderCell.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Usually, this is the header label text.
   */
  children: _propTypes.default.node,
  /**
   * Provided by `Table.Row`. Helps this cell read its alignment from the parent
   * `Table` alignments array.
   *
   * @ignore
   */
  index: _propTypes.default.number,
  /**
   * @ignore
   */
  onClick: _propTypes.default.func,
  /**
   * @ignore
   */
  onKeyDown: _propTypes.default.func,
  /**
   * Callback fired on click or keypress of this header. Called before `onClick`.
   *
   * Takes two arguments: an object `{currentSortDirection, nextSortDirection}` and `props`
   *
   * Overrides `onSortChange` set in the parent `Table.Sortable` component.
   */
  onSortChange: _propTypes.default.func,
  /**
   * Table size passed via Table's context. Used by the mixin to calculate spacing.
   *
   * @ignore
   */
  size: _propTypes.default.oneOf(Object.values(_constants.TABLE_SIZES)),
  /**
   * The direction this column is currently sorted by.
   *
   * If included, indicates that this column is sortable and adds the appropriate
   * hover styles.
   *
   * This value is automatically set when you use `Table.Sortable`.
   */
  sortDirection: _propTypes.default.oneOf(Object.values(_constants.TABLE_SORT_DIRECTIONS)),
  /**
   * An array that dictates the order of sort directions.
   *
   * When you first click on a sortable header cell, the sort direction is determined by
   * the first value in the array. Clicking on the header cell again sorts in the
   * direction determined by the second value and so on. Once you reach the end of the
   * array, we rotate back to the first value.
   *
   * If you click on a different header cell
   * and then come back to this one, the sequence begins again with the first value in the
   * array.
   */
  sortOrder: _propTypes.default.arrayOf(_propTypes.default.string)
} : {};
TableSortableHeaderCell.defaultProps = {
  sortOrder: [_constants.TABLE_SORT_DIRECTIONS.ascending, _constants.TABLE_SORT_DIRECTIONS.descending]
};